import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { authService } from "../services/authService";
import { DEFAULT_SCHOOL_EMAIL_DOMAIN } from "../utils/schoolEmail";

/** The school's login convention: first initial + last name, so Joan Nakato
 * becomes jnakato. Anything that is not a letter or a digit is dropped, so a
 * name like O'Brien still gives a login somebody can type at sign-in.
 *
 * This is what a screen can work out for itself, and it fills the field the
 * moment a name is typed. Only the server can say whether jnakato is already
 * somebody, so it is the server that adds the counter -- jnakato2 -- and that
 * answer replaces this one when it arrives. */
export function buildUsernameBase(firstName: string, lastName: string) {
  const strip = (value: string) =>
    value.toLowerCase().replace(/[^a-z0-9]/g, "");
  const first = strip(firstName);
  const last = strip(lastName);
  return first && last ? `${first[0]}${last}` : last || first;
}

/** Ask the server what to call the person whose name is being typed.
 *
 * Used by every screen that creates an account -- staff, learners, and the
 * parents admitted alongside them -- so all three name people the same way.
 * The caller decides how to put the answer into its own form, since what
 * holds the field differs from screen to screen.
 */
export function useUsernameSuggestion(firstName: string, lastName: string) {
  const [settledName, setSettledName] = useState({ first: "", last: "" });

  // Wait for a pause in the typing so the directory is not asked about every
  // half-written name.
  useEffect(() => {
    const timer = window.setTimeout(
      () => setSettledName({ first: firstName.trim(), last: lastName.trim() }),
      350,
    );
    return () => window.clearTimeout(timer);
  }, [firstName, lastName]);

  const suggestion = useQuery({
    queryKey: ["username-suggestion", settledName],
    queryFn: () =>
      authService.suggestUsername(settledName.first, settledName.last),
    enabled: Boolean(settledName.first || settledName.last),
  });

  return {
    base: buildUsernameBase(firstName, lastName),
    suggested: suggestion.data?.username || "",
    emailDomain: suggestion.data?.email_domain || DEFAULT_SCHOOL_EMAIL_DOMAIN,
  };
}

/** What to tell somebody about the login that was filled in for them. */
export function usernameHint(base: string, suggested: string, edited: boolean) {
  if (edited) return "You are setting this login yourself.";
  const suffix =
    suggested && base && suggested.startsWith(base)
      ? suggested.slice(base.length)
      : "";
  return suffix
    ? `${base} already belongs to someone, so this login ends in ${suffix} to tell them apart.`
    : "Built from the name: first initial + last name. Type here to set it yourself.";
}
